import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  X, Trash2, Download, Mail, Phone, MapPin,
  Briefcase, Package, FileSignature, Send,
  CheckCircle, XCircle, Clock, RotateCcw, Gift,
  Calendar, UserX, Eye, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { ImageLightbox } from "@/components/ImageLightbox";
import { ConsignmentWorkflowCard } from "@/components/admin/sections/ConsignmentWorkflowCard";

const API = process.env.REACT_APP_BACKEND_URL;

// Helper to construct full photo URL
const getPhotoUrl = (photoPath) => {
  if (!photoPath) return '';
  // If already a full URL, return as-is
  if (photoPath.startsWith('http://') || photoPath.startsWith('https://')) {
    return photoPath;
  }
  // If path already has /api prefix, use API URL directly
  if (photoPath.startsWith('/api/')) {
    return `${API}${photoPath}`;
  }
  // For old paths without /api prefix, add it
  if (photoPath.startsWith('/uploads/')) {
    return `${API}/api${photoPath}`;
  }
  // Default: prepend API URL
  return `${API}${photoPath}`;
};

export default function FormSubmissionModal({
  submission,
  onClose,
  onDelete,
  onDownload,
  formatSubmissionDate,
  refreshData
}) {
  const [lightbox, setLightbox] = useState(null);
  // Approval form state - default rejected_items_action to consignor's preference if available
  const [approvalForm, setApprovalForm] = useState({
    approval_status: submission?.approval_status || "approved",
    items_accepted: submission?.items_accepted || 0,
    rejected_items_action: submission?.rejected_items_action || submission?.rejected_items_preference || "return",
    admin_notes: submission?.admin_notes || ""
  });
  const [submittingApproval, setSubmittingApproval] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  
  // Rejection states
  const [showRejectionPreview, setShowRejectionPreview] = useState(false);
  const [rejectionPreview, setRejectionPreview] = useState(null);
  const [loadingRejectionPreview, setLoadingRejectionPreview] = useState(false);
  const [sendingRejection, setSendingRejection] = useState(false);

  if (!submission) return null;

  // Get auth header for API calls
  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  // Send scheduling invite using the new scheduler system
  const handleSendSchedulerInvite = async () => {
    setSendingInvite(true);
    try {
      await axios.post(
        `${API}/api/interview-scheduler/admin/send-invite/${submission.id}`,
        {},
        getAuthHeader()
      );
      toast.success(`Scheduling link sent to ${submission.full_name}!`);
      // Update local state to show invite was sent
      submission.scheduler_invite_sent = true;
      submission.scheduler_invite_sent_at = new Date().toISOString();
      if (refreshData) refreshData();
    } catch (error) {
      console.error("Error sending scheduler invite:", error);
      toast.error("Failed to send invite. Please try again.");
    } finally {
      setSendingInvite(false);
    }
  };

  // Load rejection preview
  const handleLoadRejectionPreview = async () => {
    setLoadingRejectionPreview(true);
    try {
      const response = await axios.get(
        `${API}/api/admin/forms/job-applications/${submission.id}/rejection-preview`,
        getAuthHeader()
      );
      setRejectionPreview(response.data);
      setShowRejectionPreview(true);
    } catch (error) {
      console.error("Error loading rejection preview:", error);
      toast.error("Failed to load rejection preview");
    } finally {
      setLoadingRejectionPreview(false);
    }
  };

  // Send rejection email
  const handleSendRejection = async () => {
    setSendingRejection(true);
    try {
      await axios.post(
        `${API}/api/admin/forms/job-applications/${submission.id}/send-rejection`,
        {},
        getAuthHeader()
      );
      toast.success(`Rejection email sent to ${submission.full_name}`);
      submission.status = "rejected";
      setShowRejectionPreview(false);
      if (refreshData) refreshData();
    } catch (error) {
      console.error("Error sending rejection:", error);
      const errorMsg = error.response?.data?.detail || "Failed to send rejection email";
      toast.error(errorMsg);
    } finally {
      setSendingRejection(false);
    }
  };

  // Handle approval submission
  const handleApproval = async () => {
    setSubmittingApproval(true);
    try {
      const endpoint = submission.formType === 'consignment_agreements'
        ? `${API}/api/admin/forms/consignment-agreements/${submission.id}/approve`
        : `${API}/api/admin/forms/item-additions/${submission.id}/approve`;
      
      await axios.put(endpoint, approvalForm, getAuthHeader());
      
      toast.success(
        approvalForm.approval_status === 'approved' 
          ? `Approved! ${approvalForm.items_accepted} items accepted for consignment`
          : `Submission marked as rejected`
      );
      
      // Refresh the data
      if (refreshData) refreshData();
      onClose();
    } catch (error) {
      console.error("Approval error:", error);
      toast.error("Failed to process approval. Please try again.");
    } finally {
      setSubmittingApproval(false);
    }
  };

  // Get approval status badge
  const getApprovalStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full bg-green-100 text-green-700">
            <CheckCircle className="w-4 h-4" /> Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full bg-red-100 text-red-700">
            <XCircle className="w-4 h-4" /> Rejected
          </span>
        );
      case 'pending':
      default:
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full bg-yellow-100 text-yellow-700">
            <Clock className="w-4 h-4" /> Pending Review
          </span>
        );
    }
  };

  const getGradientClass = () => {
    switch (submission.formType) {
      case "job_applications":
        return "bg-gradient-to-r from-[#FF1493] to-[#E91E8C]";
      case "consignment_inquiries":
        return "bg-gradient-to-r from-[#00D4FF] to-[#00A8CC]";
      case "item_additions":
        return "bg-gradient-to-r from-[#10B981] to-[#059669]";
      default:
        return "bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9]";
    }
  };

  const getIcon = () => {
    switch (submission.formType) {
      case "job_applications":
        return <Briefcase className="w-6 h-6" />;
      case "consignment_inquiries":
        return <Package className="w-6 h-6" />;
      case "item_additions":
        return <Package className="w-6 h-6" />;
      default:
        return <FileSignature className="w-6 h-6" />;
    }
  };

  const getFormTypeLabel = () => {
    switch (submission.formType) {
      case "job_applications":
        return "Job Application";
      case "consignment_inquiries":
        return "Consignment Inquiry";
      case "item_additions":
        return "Add Items Request";
      default:
        return "Consignment Agreement";
    }
  };

  // Generate pre-populated email based on form type
  const getEmailContent = () => {
    const firstName = submission.full_name?.split(' ')[0] || 'there';
    
    switch (submission.formType) {
      case "job_applications":
        return {
          subject: `Re: Your Job Application - Thrifty Curator`,
          body: `Hi ${firstName},

Thank you for your interest in joining the Thrifty Curator team! We have received your application and are excited to learn more about you.

We are currently reviewing applications and will be in touch soon regarding next steps.

In the meantime, if you have any questions, please don't hesitate to reach out.

Best regards,
Thrifty Curator Team`
        };
      case "consignment_inquiries":
        return {
          subject: `Re: Your Consignment Inquiry - Thrifty Curator`,
          body: `Hi ${firstName},

Thank you for reaching out about consigning with Thrifty Curator! We're excited to hear about the items you'd like to consign.

Based on your inquiry, we'd love to schedule a time to review your items. Here's what happens next:

1. We'll arrange a time for you to bring in your items for evaluation
2. Our team will assess each piece for quality and market value
3. We'll provide you with a consignment agreement outlining terms

Please let us know your availability for an appointment, and we'll get you scheduled.

Looking forward to working with you!

Best regards,
Thrifty Curator Team`
        };
      default: // consignment_agreements
        return {
          subject: `Re: Your Consignment Agreement - Thrifty Curator`,
          body: `Hi ${firstName},

Thank you for signing the consignment agreement with Thrifty Curator! We're thrilled to partner with you.

Your agreement has been received and processed. Here's what to expect:

1. Your items will be photographed and listed in our store
2. You'll receive updates when items sell
3. Payments will be processed according to the agreed schedule

If you have any questions about your agreement or would like to add more items, please don't hesitate to reach out.

Thank you for choosing Thrifty Curator!

Best regards,
Thrifty Curator Team`
        };
    }
  };

  const handleSendEmail = () => {
    const { subject, body } = getEmailContent();
    // Use current_email if available (from enriched backend data), otherwise fall back to submission email
    const emailToUse = submission.current_email || submission.email;
    
    // Replace newlines with %0D%0A for better Gmail compatibility
    const formattedBody = body.replace(/\n/g, '\r\n');
    const mailtoLink = `mailto:${emailToUse}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(formattedBody)}`;
    
    // Show reminder to select correct From account
    toast.info(
      "Remember to send from thriftycurator1@gmail.com", 
      { 
        description: "Tap the 'From' field in your email app to change the sender account.",
        duration: 5000 
      }
    );
    
    window.open(mailtoLink, '_blank');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto"
      onClick={onClose}
    >
      {lightbox && <ImageLightbox images={lightbox.images} index={lightbox.index} onClose={() => setLightbox(null)} />}
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl w-full max-w-2xl shadow-xl my-8 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="submission-details-modal"
      >
        {/* Modal Header */}
        <div className={`p-6 ${getGradientClass()} text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {getIcon()}
              <div>
                <h2 className="font-playfair text-xl font-bold">{submission.full_name}</h2>
                <p className="text-sm opacity-80">{getFormTypeLabel()}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-white/20 hover:bg-[#FF1493] rounded-full flex items-center justify-center text-white transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {submission.formType === "consignment_agreements" && (
          <ConsignmentWorkflowCard getAuthHeader={getAuthHeader} />
        )}

        {/* Action Buttons Bar */}
        <div className="flex items-center justify-between gap-2 px-6 py-3 bg-[#F9F6F7] border-b">
          {/* Left side - Invite & Reject buttons for job applications */}
          <div className="flex items-center gap-2">
            {submission.formType === "job_applications" && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSendSchedulerInvite}
                  disabled={sendingInvite}
                  className={`${
                    submission.scheduler_invite_sent 
                      ? "text-green-600 border-green-600 bg-green-50" 
                      : "text-[#8B5CF6] border-[#8B5CF6] hover:bg-[#8B5CF6]/10"
                  }`}
                  data-testid="invite-to-meet-btn"
                >
                  <Calendar className="w-4 h-4 mr-1" />
                  {sendingInvite ? "Sending..." : submission.scheduler_invite_sent ? "Resend Scheduling Link" : "Send Scheduling Link"}
                </Button>
                
                {/* Send Rejection Button */}
                {submission.status !== "rejected" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLoadRejectionPreview}
                    disabled={loadingRejectionPreview}
                    className="text-orange-600 border-orange-600 hover:bg-orange-50"
                    data-testid="send-rejection-btn"
                  >
                    {loadingRejectionPreview ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <UserX className="w-4 h-4 mr-1" />
                    )}
                    {loadingRejectionPreview ? "Loading..." : "Not Moving Forward"}
                  </Button>
                )}
                
                {submission.status === "rejected" && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full bg-orange-100 text-orange-700">
                    <UserX className="w-4 h-4" /> Rejection Sent
                  </span>
                )}
              </>
            )}
          </div>
          
          {/* Right side - Standard actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDownload && onDownload(submission)}
              className="text-[#10B981] border-[#10B981] hover:bg-[#10B981]/10"
            >
              <Download className="w-4 h-4 mr-1" /> Download
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSendEmail}
              className="text-[#00D4FF] border-[#00D4FF] hover:bg-[#00D4FF]/10"
            >
              <Send className="w-4 h-4 mr-1" /> Email
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                onDelete(submission.formType, submission.id);
                onClose();
              }}
              className="text-red-500 border-red-500 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {/* Invited Badge */}
          {submission.invited && (
            <div className="mb-4 flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
              <span className="px-2 py-1 bg-emerald-500 text-white rounded text-xs font-medium">Invited</span>
              <span className="text-emerald-700 text-sm">
                This application was submitted via invite link
                {submission.invite_template && ` (${submission.invite_template === 'onboarding' ? 'Onboarding' : 'General'})`}
              </span>
            </div>
          )}

          {/* Contact Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="flex items-center gap-3 p-3 bg-[#F9F6F7] rounded-xl">
              <Mail className="w-5 h-5 text-[#888]" />
              <div>
                <p className="text-xs text-[#888]">Email</p>
                <a href={`mailto:${submission.current_email || submission.email}`} className="text-[#333] hover:text-[#8B5CF6]">
                  {submission.current_email || submission.email}
                </a>
                {submission.current_email && submission.current_email !== submission.email && (
                  <p className="text-xs text-blue-500 mt-0.5">Updated from: {submission.email}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-[#F9F6F7] rounded-xl">
              <Phone className="w-5 h-5 text-[#888]" />
              <div>
                <p className="text-xs text-[#888]">Phone</p>
                {submission.phone ? (
                  <a href={`tel:${submission.current_phone || submission.phone}`} className="text-[#333] hover:text-[#8B5CF6]">
                    {submission.current_phone || submission.phone}
                  </a>
                ) : (
                  <span className="text-[#888]">Not provided</span>
                )}
                {/* Preferred Contact Method */}
                {submission.preferred_contact_method && (
                  <div className="mt-2 text-sm">
                    <span className="text-[#888]">Prefers: </span>
                    <span className="text-[#8B5CF6] font-medium">
                      {submission.preferred_contact_method === 'call' && 'Phone Call'}
                      {submission.preferred_contact_method === 'text' && 'Text (SMS)'}
                      {submission.preferred_contact_method === 'whatsapp_call' && 'WhatsApp Call'}
                      {submission.preferred_contact_method === 'whatsapp_text' && 'WhatsApp Text'}
                      {submission.preferred_contact_method === 'email' && 'Email'}
                      {submission.preferred_contact_method === 'app_messages' && 'Messages via Thrifty Curator App'}
                    </span>
                    {submission.preferred_contact_details && (
                      <span className="text-[#333] ml-1">({submission.preferred_contact_details})</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {submission.address && (
            <div className="flex items-start gap-3 p-3 bg-[#F9F6F7] rounded-xl mb-6">
              <MapPin className="w-5 h-5 text-[#888] mt-0.5" />
              <div>
                <p className="text-xs text-[#888]">Address</p>
                <p className="text-[#333]">{submission.address}</p>
              </div>
            </div>
          )}

          {/* Job Application Details */}
          {submission.formType === "job_applications" && (
            <div className="space-y-4">
              {/* Address */}
              {submission.address && (
                <div>
                  <Label className="text-sm font-medium text-[#666] mb-2 block">Address</Label>
                  <p className="text-[#333] bg-[#F9F6F7] p-4 rounded-xl">{submission.address}</p>
                </div>
              )}
              
              {submission.resume_text && (
                <div>
                  <Label className="text-sm font-medium text-[#666] mb-2 block">Resume / Experience</Label>
                  <p className="text-[#333] bg-[#F9F6F7] p-4 rounded-xl whitespace-pre-wrap">{submission.resume_text}</p>
                </div>
              )}
              
              {/* Work History Section */}
              {submission.work_history && submission.work_history.length > 0 && (
                <div>
                  <Label className="text-sm font-medium text-[#666] mb-2 block flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    Work History
                  </Label>
                  <div className="space-y-3">
                    {submission.work_history.map((job, index) => (
                      <div key={index} className="bg-[#F9F6F7] p-4 rounded-xl border-l-4 border-[#8B5CF6]">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-semibold text-[#333]">{job.employer || "Not provided"}</p>
                            <p className="text-sm text-[#666]">{job.title || "No title"}</p>
                          </div>
                          <span className="text-xs text-[#888] bg-white px-2 py-1 rounded">
                            {job.dates_from || "?"} - {job.dates_to || "?"}
                          </span>
                        </div>
                        {job.employer_address && (
                          <p className="text-sm text-[#666] flex items-center gap-1 mb-1">
                            <MapPin className="w-3 h-3" /> {job.employer_address}
                          </p>
                        )}
                        {job.employer_phone && (
                          <p className="text-sm text-[#666] flex items-center gap-1 mb-2">
                            <Phone className="w-3 h-3" /> {job.employer_phone}
                          </p>
                        )}
                        {job.responsibilities && (
                          <div className="mb-2">
                            <p className="text-xs text-[#888]">Responsibilities:</p>
                            <p className="text-sm text-[#333]">{job.responsibilities}</p>
                          </div>
                        )}
                        {job.reason_for_leaving && (
                          <div className="mb-2">
                            <p className="text-xs text-[#888]">Reason for leaving:</p>
                            <p className="text-sm text-[#333]">{job.reason_for_leaving}</p>
                          </div>
                        )}
                        <div className="mt-2 pt-2 border-t border-[#E0E0E0]">
                          <span className={`text-xs px-2 py-1 rounded ${job.may_contact ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {job.may_contact ? "OK to contact" : "Do not contact"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {submission.why_join && (
                <div>
                  <Label className="text-sm font-medium text-[#666] mb-2 block">Why Join Us?</Label>
                  <p className="text-[#333] bg-[#F9F6F7] p-4 rounded-xl">{submission.why_join}</p>
                </div>
              )}
              {submission.availability && (
                <div>
                  <Label className="text-sm font-medium text-[#666] mb-2 block">Availability</Label>
                  <p className="text-[#333] bg-[#F9F6F7] p-4 rounded-xl">{submission.availability}</p>
                </div>
              )}
              {submission.tasks_able_to_perform?.length > 0 && (
                <div>
                  <Label className="text-sm font-medium text-[#666] mb-2 block">Tasks Able to Perform</Label>
                  <div className="flex flex-wrap gap-2">
                    {submission.tasks_able_to_perform.map((task) => (
                      <span key={task} className="px-3 py-1 bg-[#F8C8DC]/30 text-[#5D4037] rounded-full text-sm">
                        {task}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Additional Info */}
              {submission.additional_info && (
                <div>
                  <Label className="text-sm font-medium text-[#666] mb-2 block">Additional Information</Label>
                  <p className="text-[#333] bg-[#F9F6F7] p-4 rounded-xl whitespace-pre-wrap">{submission.additional_info}</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-[#F9F6F7] rounded-xl">
                  <p className="text-xs text-[#888]">Background Check Consent</p>
                  <p className={`font-medium ${submission.background_check_consent ? 'text-green-600' : 'text-red-600'}`}>
                    {submission.background_check_consent ? "Yes" : "No"}
                  </p>
                </div>
                <div className="p-3 bg-[#F9F6F7] rounded-xl">
                  <p className="text-xs text-[#888]">Reliable Transportation</p>
                  <p className={`font-medium ${submission.has_reliable_transportation ? 'text-green-600' : 'text-red-600'}`}>
                    {submission.has_reliable_transportation ? "Yes" : "No"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Consignment Inquiry Details */}
          {submission.formType === "consignment_inquiries" && (
            <div className="space-y-4">
              {submission.item_types?.length > 0 && (
                <div>
                  <Label className="text-sm font-medium text-[#666] mb-2 block">Item Types</Label>
                  <div className="flex flex-wrap gap-2">
                    {submission.item_types.map((type) => (
                      <span key={type} className="px-3 py-1 bg-[#00D4FF]/20 text-[#00A8CC] rounded-full text-sm">
                        {type}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {submission.other_item_type && (
                <div>
                  <Label className="text-sm font-medium text-[#666] mb-2 block">Other Item Type</Label>
                  <p className="text-[#333] bg-[#F9F6F7] p-4 rounded-xl">{submission.other_item_type}</p>
                </div>
              )}
              {submission.item_description && (
                <div>
                  <Label className="text-sm font-medium text-[#666] mb-2 block">Item Description</Label>
                  <p className="text-[#333] bg-[#F9F6F7] p-4 rounded-xl whitespace-pre-wrap">{submission.item_description}</p>
                </div>
              )}
              {submission.item_condition && (
                <div>
                  <Label className="text-sm font-medium text-[#666] mb-2 block">Item Condition</Label>
                  <p className="text-[#333] bg-[#F9F6F7] p-4 rounded-xl">{submission.item_condition}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-[#F9F6F7] rounded-xl">
                  <p className="text-xs text-[#888]">Smoke-Free Environment</p>
                  <p className={`font-medium ${submission.smoke_free ? 'text-green-600' : 'text-orange-600'}`}>
                    {submission.smoke_free ? "Yes" : "No"}
                  </p>
                </div>
                <div className="p-3 bg-[#F9F6F7] rounded-xl">
                  <p className="text-xs text-[#888]">Pet-Free Environment</p>
                  <p className={`font-medium ${submission.pet_free ? 'text-green-600' : 'text-orange-600'}`}>
                    {submission.pet_free ? "Yes" : "No"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Consignment Agreement Details */}
          {submission.formType === "consignment_agreements" && (
            <div className="space-y-4">
              {/* Photos Section */}
              {submission.photos && submission.photos.length > 0 && (
                <div>
                  <Label className="text-sm font-medium text-[#666] mb-2 block">
                    Submitted Photos ({submission.photos.length})
                  </Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {submission.photos.map((photo, idx) => (
                      <button
                        type="button"
                        key={idx} 
                        onClick={() => setLightbox({ images: submission.photos.map(getPhotoUrl), index: idx })}
                        className="block w-full aspect-square rounded-lg overflow-hidden border border-[#eee] hover:border-[#8B5CF6] transition-colors"
                        data-testid={`submission-photo-${idx}`}
                      >
                        <img 
                          src={getPhotoUrl(photo)} 
                          alt={`Item ${idx + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400 text-xs">Image not available</div>';
                          }}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {submission.items_description && (
                <div>
                  <Label className="text-sm font-medium text-[#666] mb-2 block">Items Description</Label>
                  <p className="text-[#333] bg-[#F9F6F7] p-4 rounded-xl whitespace-pre-wrap">{submission.items_description}</p>
                </div>
              )}
              
              {submission.additional_info && (
                <div>
                  <Label className="text-sm font-medium text-[#666] mb-2 block">Additional Information</Label>
                  <p className="text-[#333] bg-[#F9F6F7] p-4 rounded-xl whitespace-pre-wrap">{submission.additional_info}</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-[#C5A065]/10 rounded-xl">
                  <p className="text-xs text-[#888]">Agreed Percentage</p>
                  <p className="font-bold text-[#C5A065] text-lg">{submission.agreed_percentage}</p>
                </div>
                <div className="p-3 bg-[#F9F6F7] rounded-xl">
                  <p className="text-xs text-[#888]">Signature Date</p>
                  <p className="font-medium text-[#333]">{submission.signature_date || "N/A"}</p>
                </div>
              </div>
              
              {submission.payment_method && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-[#F9F6F7] rounded-xl">
                    <p className="text-xs text-[#888]">Payment Method</p>
                    <p className="font-medium text-[#333]">{submission.payment_method}</p>
                  </div>
                  {submission.payment_details && (
                    <div className="p-3 bg-[#F9F6F7] rounded-xl">
                      <p className="text-xs text-[#888]">Payment Details</p>
                      <p className="font-medium text-[#333]">{submission.payment_details}</p>
                    </div>
                  )}
                </div>
              )}
              
              {submission.signature && (
                <div>
                  <Label className="text-sm font-medium text-[#666] mb-2 block">Signature</Label>
                  <p className="text-[#333] bg-[#F9F6F7] p-4 rounded-xl font-signature text-xl italic">{submission.signature}</p>
                </div>
              )}
              <div className="p-3 bg-[#F9F6F7] rounded-xl">
                <p className="text-xs text-[#888]">Agreed to Terms</p>
                <p className={`font-medium ${submission.agreed_to_terms ? 'text-green-600' : 'text-red-600'}`}>
                  {submission.agreed_to_terms ? "Yes" : "No"}
                </p>
              </div>

              {/* Consignment Review Section */}
              <div className="mt-6 p-4 bg-gradient-to-r from-[#8B5CF6]/10 to-[#6D28D9]/10 rounded-xl border border-[#8B5CF6]/20">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-[#333] flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-[#8B5CF6]" />
                    Consignment Review
                  </h3>
                  {getApprovalStatusBadge(submission.approval_status || 'pending')}
                </div>

                {submission.approval_status && submission.approval_status !== 'pending' ? (
                  /* Show review details if already reviewed */
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-white rounded-lg">
                        <p className="text-xs text-[#888]">Items Accepted</p>
                        <p className="font-bold text-green-600 text-lg">{submission.items_accepted || 0}</p>
                      </div>
                      <div className="p-3 bg-white rounded-lg">
                        <p className="text-xs text-[#888]">Non-Consigned Items</p>
                        <p className="font-medium text-[#333] flex items-center gap-1">
                          {submission.rejected_items_action === 'donate' ? (
                            <><Gift className="w-4 h-4 text-purple-500" /> Donated</>
                          ) : (
                            <><RotateCcw className="w-4 h-4 text-blue-500" /> Returned</>
                          )}
                        </p>
                      </div>
                    </div>
                    {submission.admin_notes && (
                      <div className="p-3 bg-white rounded-lg">
                        <p className="text-xs text-[#888]">Admin Notes</p>
                        <p className="text-[#333] text-sm">{submission.admin_notes}</p>
                      </div>
                    )}
                    {submission.reviewed_at && (
                      <p className="text-xs text-[#888] text-right">
                        Reviewed: {formatSubmissionDate(submission.reviewed_at)}
                        {submission.reviewed_by && ` by ${submission.reviewed_by}`}
                      </p>
                    )}
                  </div>
                ) : (
                  /* Show approval form if pending */
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      {/* Decision */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Decision</Label>
                        <Select
                          value={approvalForm.approval_status}
                          onValueChange={(value) => setApprovalForm({ ...approvalForm, approval_status: value })}
                        >
                          <SelectTrigger className="w-full bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="approved">
                              <span className="flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-green-600" /> Approve
                              </span>
                            </SelectItem>
                            <SelectItem value="rejected">
                              <span className="flex items-center gap-2">
                                <XCircle className="w-4 h-4 text-red-600" /> Reject
                              </span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Items Accepted */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">
                          Items Accepted{submission.items_to_add > 0 ? ` (of ${submission.items_to_add})` : ''}
                        </Label>
                        <Input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={approvalForm.items_accepted === 0 ? '' : String(approvalForm.items_accepted)}
                          placeholder="Enter number"
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '') {
                              setApprovalForm({ ...approvalForm, items_accepted: 0 });
                            } else {
                              const num = parseInt(val);
                              const maxItems = submission.items_to_add > 0 ? submission.items_to_add : 9999;
                              if (!isNaN(num) && num >= 0 && num <= maxItems) {
                                setApprovalForm({ ...approvalForm, items_accepted: num });
                              }
                            }
                          }}
                          onFocus={(e) => e.target.select()}
                          className="bg-white"
                          data-testid="items-accepted-input"
                        />
                      </div>
                    </div>

                    {/* Rejected Items Action - Only show if there's a known total and some items are not accepted */}
                    {submission.items_to_add && approvalForm.items_accepted < submission.items_to_add && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">
                        What happens to {submission.items_to_add - approvalForm.items_accepted} item(s) not consigned?
                      </Label>
                      {/* Show consignor's preference if available */}
                      {submission.rejected_items_preference && (
                        <p className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                          Consignor's preference: {submission.rejected_items_preference === 'donate' ? 'Donated' : 'Returned to them'}
                        </p>
                      )}
                      <Select
                        value={approvalForm.rejected_items_action}
                        onValueChange={(value) => setApprovalForm({ ...approvalForm, rejected_items_action: value })}
                      >
                        <SelectTrigger className="w-full bg-white">
                          <SelectValue placeholder="Select action..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="return">
                            <span className="flex items-center gap-2">
                              <RotateCcw className="w-4 h-4 text-blue-600" /> Return to Owner
                            </span>
                          </SelectItem>
                          <SelectItem value="donate">
                            <span className="flex items-center gap-2">
                              <Gift className="w-4 h-4 text-purple-600" /> Donate
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    )}

                    {/* Admin Notes */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Notes (Optional)</Label>
                      <Textarea
                        value={approvalForm.admin_notes}
                        onChange={(e) => setApprovalForm({ ...approvalForm, admin_notes: e.target.value })}
                        placeholder="Any notes about this review..."
                        rows={2}
                        className="bg-white"
                        data-testid="admin-notes-input"
                      />
                    </div>

                    {/* Submit Button */}
                    <Button
                      onClick={handleApproval}
                      disabled={submittingApproval}
                      className={`w-full ${approvalForm.approval_status === 'approved' 
                        ? "bg-green-600 hover:bg-green-700 text-white"
                        : "bg-red-600 hover:bg-red-700 text-white"
                      }`}
                      data-testid="submit-approval-btn"
                    >
                      {submittingApproval ? "Processing..." : (
                        approvalForm.approval_status === 'approved' 
                          ? `Approve (${approvalForm.items_accepted} items)`
                          : 'Reject Submission'
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Item Additions Details */}
          {submission.formType === "item_additions" && (
            <div className="space-y-4">
              {/* Photos Section */}
              {submission.photos && submission.photos.length > 0 && (
                <div>
                  <Label className="text-sm font-medium text-[#666] mb-2 block">
                    Submitted Photos ({submission.photos.length})
                  </Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {submission.photos.map((photo, idx) => (
                      <button
                        type="button"
                        key={idx} 
                        onClick={() => setLightbox({ images: submission.photos.map(getPhotoUrl), index: idx })}
                        className="block w-full aspect-square rounded-lg overflow-hidden border border-[#eee] hover:border-[#10B981] transition-colors"
                        data-testid={`addition-photo-${idx}`}
                      >
                        <img 
                          src={getPhotoUrl(photo)} 
                          alt={`Item ${idx + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400 text-xs">Image not available</div>';
                          }}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Items Added */}
              {submission.items_to_add > 0 && (
                <div className="p-4 bg-emerald-50 rounded-xl">
                  <p className="text-xs text-[#888] mb-1">Items to Add</p>
                  <p className="text-emerald-700 font-bold text-2xl">+{submission.items_to_add} items</p>
                </div>
              )}

              {submission.items_description && (
                <div>
                  <Label className="text-sm font-medium text-[#666] mb-2 block">Items Description</Label>
                  <p className="text-[#333] bg-[#F9F6F7] p-4 rounded-xl whitespace-pre-wrap">{submission.items_description}</p>
                </div>
              )}
              
              {submission.additional_info && (
                <div>
                  <Label className="text-sm font-medium text-[#666] mb-2 block">Additional Information</Label>
                  <p className="text-[#333] bg-[#F9F6F7] p-4 rounded-xl whitespace-pre-wrap">{submission.additional_info}</p>
                </div>
              )}

              {/* Contact Updates */}
              {(submission.update_email || submission.update_phone || submission.update_address || submission.update_payment_method) && (
                <div>
                  <Label className="text-sm font-medium text-[#666] mb-2 block">Requested Updates</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {submission.update_email && (
                      <div className="p-3 bg-blue-50 rounded-xl">
                        <p className="text-xs text-blue-600">New Email</p>
                        <p className="font-medium text-[#333]">{submission.update_email}</p>
                      </div>
                    )}
                    {submission.update_phone && (
                      <div className="p-3 bg-purple-50 rounded-xl">
                        <p className="text-xs text-purple-600">New Phone</p>
                        <p className="font-medium text-[#333]">{submission.update_phone}</p>
                      </div>
                    )}
                    {submission.update_address && (
                      <div className="p-3 bg-orange-50 rounded-xl col-span-2">
                        <p className="text-xs text-orange-600">New Address</p>
                        <p className="font-medium text-[#333]">{submission.update_address}</p>
                      </div>
                    )}
                    {submission.update_payment_method && (
                      <div className="p-3 bg-amber-50 rounded-xl">
                        <p className="text-xs text-amber-600">New Payment Method</p>
                        <p className="font-medium text-[#333]">{submission.update_payment_method}</p>
                        {submission.update_payment_details && (
                          <p className="text-sm text-[#666]">{submission.update_payment_details}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {submission.signature && (
                <div>
                  <Label className="text-sm font-medium text-[#666] mb-2 block">Signature</Label>
                  <p className="text-[#333] bg-[#F9F6F7] p-4 rounded-xl font-signature text-xl italic">{submission.signature}</p>
                  {submission.signature_date && (
                    <p className="text-xs text-[#888] mt-1">Signed on: {submission.signature_date}</p>
                  )}
                </div>
              )}

              {/* Consignment Review Section - Only show for item additions, not info-only updates */}
              {submission.items_to_add > 0 && (
                <div className="mt-6 p-4 bg-gradient-to-r from-[#10B981]/10 to-[#059669]/10 rounded-xl border border-[#10B981]/20">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-[#333] flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-[#10B981]" />
                      Consignment Review
                    </h3>
                    {getApprovalStatusBadge(submission.approval_status || 'pending')}
                  </div>

                  {submission.approval_status && submission.approval_status !== 'pending' && submission.approval_status !== 'info_update' ? (
                    /* Show review details if already reviewed */
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-white rounded-lg">
                          <p className="text-xs text-[#888]">Items Accepted</p>
                          <p className="font-bold text-green-600 text-lg">{submission.items_accepted || 0}</p>
                        </div>
                        <div className="p-3 bg-white rounded-lg">
                          <p className="text-xs text-[#888]">Non-Consigned Items</p>
                          <p className="font-medium text-[#333] flex items-center gap-1">
                            {submission.rejected_items_action === 'donate' ? (
                              <><Gift className="w-4 h-4 text-purple-500" /> Will be Donated</>
                            ) : (
                              <><RotateCcw className="w-4 h-4 text-blue-500" /> Will be Returned</>
                            )}
                          </p>
                        </div>
                      </div>
                      {submission.admin_notes && (
                        <div className="p-3 bg-white rounded-lg">
                          <p className="text-xs text-[#888]">Admin Notes</p>
                          <p className="text-[#333] text-sm">{submission.admin_notes}</p>
                        </div>
                      )}
                      {submission.reviewed_at && (
                        <p className="text-xs text-[#888] text-right">
                          Reviewed: {formatSubmissionDate(submission.reviewed_at)}
                          {submission.reviewed_by && ` by ${submission.reviewed_by}`}
                        </p>
                      )}
                    </div>
                  ) : (
                    /* Show approval form if pending */
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        {/* Decision */}
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Decision</Label>
                          <Select
                            value={approvalForm.approval_status}
                            onValueChange={(value) => setApprovalForm({ ...approvalForm, approval_status: value })}
                          >
                            <SelectTrigger className="w-full bg-white">
                              <SelectValue>
                                {approvalForm.approval_status === 'approved' ? (
                                  <span className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-green-600" /> Approve
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-2">
                                    <XCircle className="w-4 h-4 text-red-600" /> Reject
                                  </span>
                                )}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="approved">
                                <span className="flex items-center gap-2">
                                  <CheckCircle className="w-4 h-4 text-green-600" /> Approve
                                </span>
                              </SelectItem>
                              <SelectItem value="rejected">
                                <span className="flex items-center gap-2">
                                  <XCircle className="w-4 h-4 text-red-600" /> Reject
                                </span>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Items Accepted */}
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">
                            Items Accepted{submission.items_to_add > 0 ? ` (of ${submission.items_to_add})` : ''}
                          </Label>
                          <Input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={approvalForm.items_accepted === 0 ? '' : String(approvalForm.items_accepted)}
                            placeholder="Enter number"
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '') {
                                setApprovalForm({ ...approvalForm, items_accepted: 0 });
                              } else {
                                const num = parseInt(val);
                                const maxItems = submission.items_to_add > 0 ? submission.items_to_add : 9999;
                                if (!isNaN(num) && num >= 0 && num <= maxItems) {
                                  setApprovalForm({ ...approvalForm, items_accepted: num });
                                }
                              }
                            }}
                            onFocus={(e) => e.target.select()}
                            className="bg-white"
                            data-testid="items-accepted-input"
                          />
                        </div>
                      </div>

                      {/* Rejected Items Action - Only show if some items are not accepted */}
                      {submission.items_to_add > 0 && approvalForm.items_accepted < submission.items_to_add && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">
                          What happens to {submission.items_to_add - approvalForm.items_accepted} item(s) not consigned?
                        </Label>
                        {/* Show consignor's preference if available */}
                        {submission.rejected_items_preference && (
                          <p className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                            Consignor's preference: {submission.rejected_items_preference === 'donate' ? 'Donated' : 'Returned to them'}
                          </p>
                        )}
                        <Select
                          value={approvalForm.rejected_items_action}
                          onValueChange={(value) => setApprovalForm({ ...approvalForm, rejected_items_action: value })}
                        >
                          <SelectTrigger className="w-full bg-white">
                            <SelectValue>
                              {approvalForm.rejected_items_action === 'donate' ? (
                                <span className="flex items-center gap-2">
                                  <Gift className="w-4 h-4 text-purple-600" /> Donate
                                </span>
                              ) : (
                                <span className="flex items-center gap-2">
                                  <RotateCcw className="w-4 h-4 text-blue-600" /> Return to Owner
                                </span>
                              )}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="return">
                              <span className="flex items-center gap-2">
                                <RotateCcw className="w-4 h-4 text-blue-600" /> Return to Owner
                              </span>
                            </SelectItem>
                            <SelectItem value="donate">
                              <span className="flex items-center gap-2">
                                <Gift className="w-4 h-4 text-purple-600" /> Donate
                              </span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      )}

                      {/* Admin Notes */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Notes (Optional)</Label>
                        <Textarea
                          value={approvalForm.admin_notes}
                          onChange={(e) => setApprovalForm({ ...approvalForm, admin_notes: e.target.value })}
                          placeholder="Any notes about this review..."
                          rows={2}
                          className="bg-white"
                          data-testid="admin-notes-input"
                        />
                      </div>

                      {/* Submit Button */}
                      <Button
                        onClick={handleApproval}
                        disabled={submittingApproval}
                        className={`w-full ${approvalForm.approval_status === 'approved' 
                          ? "bg-green-600 hover:bg-green-700 text-white"
                          : "bg-red-600 hover:bg-red-700 text-white"
                        }`}
                        data-testid="submit-approval-btn"
                      >
                        {submittingApproval ? "Processing..." : (
                          approvalForm.approval_status === 'approved' 
                            ? `Approve (${approvalForm.items_accepted} items)`
                            : 'Reject Submission'
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Submission Meta */}
          <div className="mt-6 pt-4 border-t border-[#eee] flex items-center justify-between text-sm text-[#888]">
            <span>Submitted: {formatSubmissionDate(submission.submitted_at)}</span>
            <span>ID: {submission.id.slice(0, 8)}...</span>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-4 bg-[#F9F6F7] flex flex-col sm:flex-row justify-between items-center gap-3">
          <Button
            variant="outline"
            onClick={() => onDelete(submission.formType, submission.id)}
            className="text-red-500 border-red-200 hover:bg-red-50 w-full sm:w-auto"
            data-testid="delete-submission-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
          <div className="flex flex-wrap justify-center gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={() => onDownload(submission)}
              className="text-[#8B5CF6] border-[#8B5CF6]/30 hover:bg-[#8B5CF6]/10"
              data-testid="download-submission-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
            <Button
              onClick={onClose}
              className="btn-primary"
            >
              Close
            </Button>
          </div>
        </div>
      </motion.div>
      
      {/* Rejection Preview Modal */}
      <AnimatePresence>
        {showRejectionPreview && rejectionPreview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
            onClick={() => setShowRejectionPreview(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden"
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Eye className="w-6 h-6 text-white" />
                  <h3 className="text-lg font-semibold text-white">Preview Rejection Email</h3>
                </div>
                <button
                  onClick={() => setShowRejectionPreview(false)}
                  className="text-white/80 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Email Preview */}
              <div className="p-6">
                <div className="mb-4">
                  <p className="text-sm text-gray-500 mb-1">To:</p>
                  <p className="font-medium text-gray-900">{rejectionPreview.applicant_email}</p>
                </div>
                <div className="mb-4">
                  <p className="text-sm text-gray-500 mb-1">Subject:</p>
                  <p className="font-medium text-gray-900">{rejectionPreview.subject}</p>
                </div>
                <div className="mb-4">
                  <p className="text-sm text-gray-500 mb-2">Message Preview:</p>
                  <div className="bg-gray-50 rounded-xl p-4 max-h-[300px] overflow-y-auto">
                    <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">
                      {rejectionPreview.preview_text}
                    </pre>
                  </div>
                </div>
                
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-orange-800">
                    <strong>Note:</strong> The email will include buttons for the applicant to choose whether to keep their application on file for future opportunities.
                  </p>
                </div>
              </div>
              
              {/* Actions */}
              <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowRejectionPreview(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSendRejection}
                  disabled={sendingRejection}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                >
                  {sendingRejection ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Rejection Email
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
